import type { VoidFunctionComponent } from 'react';

import { useLicense } from '../../../../hooks/api/licenses';
import type { LicenseResponse } from '../../../../hooks/api/licenses/useLicense';

import * as Loading from './Loading';

import styles from './FileViewer.module.less';

//  This is what is actually returned in the hook,
//  which is a slimmed down version of the full License type.
type LicenseData = {
  name: string;
  description?: string;
};

/**
 * Takes a license response and slims it down to the LicenseData type.
 * Will try to use the included `Summary` if available, otherwise falls back to `licenseComments`.
 */
function formatLicense({ name, Summary, licenseComments }: LicenseResponse): LicenseData {
  return {
    name,
    description: Summary?.summary || licenseComments,
  };
}

//  We don't need the full license here, this helper just edits the returned data
function useTruncatedLicense(licenseId: string) {
  const response = useLicense(licenseId);

  return {
    ...response,
    data: response.data
      ? formatLicense(response.data)
      : response.data,
  };
}

type Props = {
  licenseId: string;
};

/**
 * FileViewerLicense displays the full title of the license
 * and the description (if available).
 */
const FileViewerLicense: VoidFunctionComponent<Props> = ({ licenseId }) => {
  const { isLoading, data: license } = useTruncatedLicense(licenseId);
  return (
    <div className={styles.license}>
      {isLoading ? (
        <Loading.License />
      ) : (
        <>
          <h2 className={styles.licenseHeading}>
            {license?.name ?? licenseId}
          </h2>
          {license?.description && (
            <p className={styles.licenseDescription}>
              {license.description}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default FileViewerLicense;
